import { useState } from 'react';
import { X, Warning, ArrowsClockwise, LinkSimple } from '@phosphor-icons/react';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { useModelos } from '@/hooks/useLotesKanban';
import { useReTramite } from '@/hooks/useBootstrap';
import { fmtDate } from '@/lib/utils';
import type { ApiError } from '@/api/client';
import type { TramiteVigenteInfo } from '@/types';
import type { ProyectoCode } from '@/types/sitePlan';

// Lo que se esta vinculando. NO es un `BootstrapGrupo` a proposito: desde la
// tarjeta de LOTE puede ser un SUBCONJUNTO de varios codigos a la vez (los que
// el operador marco como el mismo trámite), y desde la tarjeta de codigo es uno
// solo. El dialogo solo necesita la union de idsCorreo + lo que describe el
// conjunto para el panel "lo que estás vinculando".
export type LoQueVinculas = {
  idsCorreo: number[];
  etiqueta: string;
  cantidadCorreos: number;
  tipos: string[];
  textosLoteRaw: string[];
};

type Props = {
  vinculando: LoQueVinculas;
  idLote: number;
  idBd?: string | null;
  // Proyecto del lote (para filtrar el combo de modelo del camino 'nuevo').
  proyecto?: string | null;
  // Prefill del modelo en 'nuevo' (el que venia del panel de vinculacion).
  idModeloInicial?: number | null;
  // Datos del tramite VIEJO (del 409 que detecto el GrupoCard).
  tramiteVigente: TramiteVigenteInfo;
  onClose: () => void;
};

// Dialogo de re-tramite (9.6). Se abre SOLO desde el boton "Resolver re-tramite"
// del GrupoCard (que aparece cuando el vincular devolvio 409). Muestra el
// tramite viejo vs el grupo nuevo y ofrece los dos caminos con peso visual
// ASIMETRICO: 'mismo' es seguro (no destruye) -> CTA verde directo; 'nuevo'
// jubila el vigente (irreversible) -> rojo + confirmacion de dos pasos.
export function ReTramiteDialog({
  vinculando, idLote, idBd, proyecto, idModeloInicial, tramiteVigente, onClose,
}: Props) {
  const viejoNum = tramiteVigente.idTramite;

  // Modelo para el camino 'nuevo': arranca con el del panel de vinculacion, o
  // el del tramite viejo como fallback. Editable.
  const [idModelo, setIdModelo] = useState<number | null>(idModeloInicial ?? tramiteVigente.idModelo ?? null);
  // Confirmacion de dos pasos del camino destructivo.
  const [confirmNuevo, setConfirmNuevo] = useState(false);
  // Copiar el modelo al «Modelo Tramitado» del lote (Lotes.IDModDefecto). Solo
  // para el camino 'nuevo': 'mismo' no toca modelo ni estado, a proposito.
  const [aplicarAlLote, setAplicarAlLote] = useState(false);

  const { data: modelos = [] } = useModelos((proyecto ?? null) as ProyectoCode | null);
  const modeloOptions = modelos.map(m => ({ value: m.idModelo, label: m.nombre ?? `Modelo ${m.idModelo}` }));

  const reTramite = useReTramite();
  // Error DENTRO del dialogo (no cierra): mismo ApiError (status+body). El caso
  // tipico es concurrencia (otro operador jubilo el viejo / el lote ya no tiene
  // vigente) -> 9.1. El operador lo lee y reintenta o cancela.
  const err = reTramite.error as ApiError | null;
  const errorMsg = reTramite.isError ? (err?.message ?? 'Error al re-tramitar') : null;

  // El cierre SOLO ocurre al exito (onSuccess por-llamada). onError deja abierto.
  function doMismo() {
    reTramite.mutate({ idsCorreo: vinculando.idsCorreo, idLote, camino: 'mismo' }, { onSuccess: onClose });
  }
  function doNuevo() {
    reTramite.mutate({
      idsCorreo: vinculando.idsCorreo, idLote, camino: 'nuevo', idModelo,
      aplicarModeloAlLote: idModelo != null && aplicarAlLote,
    }, { onSuccess: onClose });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-2xl rounded-2xl bg-white p-5 shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <ArrowsClockwise size={20} weight="bold" className="shrink-0 text-brand-yellow" />
            <h2 className="text-lg font-semibold text-brand-gray-500">
              Re-trámite — el lote {idBd ?? `#${idLote}`} ya tiene trámite vigente
            </h2>
          </div>
          <button onClick={onClose} className="rounded p-1 text-brand-gray-400 hover:bg-brand-gray-100" aria-label="Cerrar">
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Comparacion viejo vs nuevo (9.6: mostrar el viejo ANTES de decidir) */}
        <div className="mb-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-brand-gray-200 bg-brand-bg p-3">
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-brand-gray-400">
              Trámite vigente actual
            </div>
            <div className="flex flex-col gap-1 text-sm text-brand-gray-500">
              <div className="font-mono font-semibold">Trámite #{viejoNum}</div>
              <div className="font-mono text-xs">
                {tramiteVigente.codigoApc && <>APC {tramiteVigente.codigoApc} </>}
                {tramiteVigente.codigoCfia && <>CFIA {tramiteVigente.codigoCfia}</>}
                {!tramiteVigente.codigoApc && !tramiteVigente.codigoCfia && <span className="italic">sin código</span>}
              </div>
              <div>Modelo: {tramiteVigente.modeloActual ?? <span className="italic text-brand-gray-400">—</span>}</div>
              <div>
                Estado: {tramiteVigente.estadoVigente ?? '—'}
                {tramiteVigente.faseVigente ? ` · ${tramiteVigente.faseVigente}` : ''}
              </div>
              {tramiteVigente.fechaEstadoVigente && (
                <div className="text-xs text-brand-gray-400">Desde {fmtDate(tramiteVigente.fechaEstadoVigente)}</div>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-brand-gray-200 bg-white p-3">
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-brand-gray-400">
              Lo que estás vinculando
            </div>
            <div className="flex flex-col gap-1 text-sm text-brand-gray-500">
              <div className="font-mono font-semibold">{vinculando.etiqueta}</div>
              <div className="text-xs">{vinculando.cantidadCorreos} {vinculando.cantidadCorreos === 1 ? 'correo' : 'correos'}</div>
              {vinculando.tipos.length > 0 && <div className="text-xs text-brand-gray-400">{vinculando.tipos.join(', ')}</div>}
              {vinculando.textosLoteRaw.length > 0 && (
                <div className="font-mono text-xs">Texto: {vinculando.textosLoteRaw.join(' · ')}</div>
              )}
            </div>
          </div>
        </div>

        {/* Decision: dos caminos con peso visual ASIMETRICO */}
        <div className="grid gap-3 sm:grid-cols-2">
          {/* MISMO (seguro, no destruye) — CTA verde directo */}
          <div className="flex flex-col gap-2 rounded-lg border border-brand-gray-200 p-3">
            <div className="text-sm font-semibold text-brand-gray-500">Mismo trámite (otro código)</div>
            <p className="text-[11px] text-brand-gray-400">
              Son del mismo trámite, vinculándose con su otro código (APC↔CFIA). Asocia los correos
              al trámite #{viejoNum} sin crear ni cambiar estado, y completa el código que le falte.
            </p>
            <button
              onClick={doMismo}
              disabled={reTramite.isPending}
              className="mt-auto inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-green-100 px-3 py-2 text-sm font-semibold text-black hover:bg-brand-green-200 disabled:opacity-50"
            >
              <LinkSimple size={15} weight="bold" /> Asociar al trámite #{viejoNum}
            </button>
          </div>

          {/* NUEVO (destructivo, irreversible) — rojo + confirmacion de 2 pasos */}
          <div className="flex flex-col gap-2 rounded-lg border border-brand-red-100 bg-brand-red-100/5 p-3">
            <div className="text-sm font-semibold text-brand-red-200">Trámite nuevo (cliente/modelo cambió)</div>
            <p className="text-[11px] text-brand-gray-500">
              El cliente cambió: <span className="font-semibold">jubila el trámite #{viejoNum}</span> (dejará de
              ser el activo) y crea uno nuevo en su estado inicial. <span className="font-semibold">Acción irreversible.</span>
            </p>
            <div className="flex items-center gap-2">
              <span className="shrink-0 text-[11px] font-medium text-brand-gray-500">Modelo del trámite</span>
              <div className="min-w-0 flex-1">
                <SearchableSelect<number>
                  options={modeloOptions}
                  value={idModelo}
                  onChange={setIdModelo}
                  placeholder="— Sin modelo —"
                  emptyText="Sin modelos activos"
                />
              </div>
            </div>

            {idModelo != null && (
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="checkbox"
                  checked={aplicarAlLote}
                  onChange={e => setAplicarAlLote(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 cursor-pointer accent-brand-green-100"
                />
                <span className="text-[11px] leading-tight text-brand-gray-500">
                  Guardar también como <span className="font-semibold">Modelo Tramitado del lote</span>
                </span>
              </label>
            )}

            {!confirmNuevo ? (
              // Paso 1: intencion. Botoneo rojo OUTLINE (menos accesible que el verde solido).
              <button
                onClick={() => setConfirmNuevo(true)}
                disabled={reTramite.isPending}
                className="mt-auto inline-flex items-center justify-center gap-1.5 rounded-lg border border-brand-red-200 px-3 py-2 text-sm font-semibold text-brand-red-200 hover:bg-brand-red-100/10 disabled:opacity-50"
              >
                <Warning size={15} weight="bold" /> Crear nuevo — jubilar #{viejoNum}
              </button>
            ) : (
              // Paso 2: confirmacion explicita. Solido rojo.
              <div className="mt-auto flex gap-2">
                <button
                  onClick={() => setConfirmNuevo(false)}
                  disabled={reTramite.isPending}
                  className="rounded-lg border border-brand-gray-300 px-2.5 py-2 text-xs text-brand-gray-500 hover:bg-brand-gray-100 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={doNuevo}
                  disabled={reTramite.isPending}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-brand-red-200 px-3 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                >
                  <Warning size={15} weight="bold" />
                  {reTramite.isPending ? 'Procesando…' : `Confirmar: jubilar #${viejoNum}`}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Error DENTRO del dialogo, NO cierra (concurrencia 9.1 / lote sin vigente) */}
        {errorMsg && (
          <div className="mt-3 flex items-start gap-1.5 rounded-lg bg-brand-red-100/15 px-2.5 py-2 text-[11px] text-brand-red-200">
            <Warning size={14} weight="bold" className="mt-0.5 shrink-0" />
            {errorMsg}
          </div>
        )}
      </div>
    </div>
  );
}
